import { captureUnsavedFeedback } from '../utils/unsavedFeedback';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCalendarAlt, FaCheck, FaChevronLeft, FaChevronRight, FaClock,
  FaDownload, FaExclamationTriangle, FaFilter, FaList, FaRegCalendarAlt,
  FaTimes, FaUser
} from 'react-icons/fa';
import './Agenda.css';

const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const WEEK_DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const TYPE_OPTIONS = ['Prescrizione', 'Promemoria', 'Promemoria prescrizione', 'Medico NS', 'Medico CTP', 'Trattativa', 'Firma quietanza', 'Attesa assegno', 'Altro'];
const pad = value => String(value).padStart(2, '0');
const dateKey = value => value ? String(value).slice(0, 10) : '';
const localKey = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const parseDate = value => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const [year, month, day] = dateKey(value).split('-').map(Number);
  return new Date(year, month - 1, day);
};
const addDays = (date, days) => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
};
const startOfWeek = date => addDays(date, -((date.getDay() + 6) % 7));
const formatDate = value => parseDate(value).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
const formatShort = value => parseDate(value).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
const personName = row => [row.cognome, row.nome].filter(Boolean).join(' ') || 'Cliente non indicato';
const normalize = value => String(value || '').trim().toLocaleLowerCase('it');
const category = type => {
  const value = normalize(type);
  if (value.includes('prescrizione')) return 'prescrizione';
  if (value.includes('medico')) return 'medico';
  if (value.includes('quietanza') || value.includes('pagamento') || value.includes('assegno')) return 'pagamento';
  if (value.includes('trattativa')) return 'trattativa';
  return 'promemoria';
};

function AgendaEvent({ row, compact = false, onOpen }) {
  const overdue = !row.completata && dateKey(row.data_scadenza) < localKey(new Date());
  return <button type="button" className={`agenda-event agenda-event--${category(row.tipo)} ${overdue ? 'is-overdue' : ''} ${row.completata ? 'is-done' : ''}`} onClick={() => onOpen(row)}>
    <span className="agenda-event-time">{row.ora ? String(row.ora).slice(0, 5) : compact ? 'Intera giornata' : formatShort(row.data_scadenza)}</span>
    <strong>{row.descrizione || row.tipo}</strong>
    <span>{personName(row)}{row.numero_progressivo ? ` · ${row.numero_progressivo}` : ''}</span>
  </button>;
}

export default function Agenda({ operator }) {
  const today = useMemo(() => new Date(), []);
  const todayKey = localKey(today);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [counts, setCounts] = useState({ today: 0, upcoming: 0, overdue: 0 });
  const [operators, setOperators] = useState([]);
  const [view, setView] = useState('list');
  const [listMode, setListMode] = useState('today');
  const [anchor, setAnchor] = useState(today);
  const [scope, setScope] = useState('all');
  const [type, setType] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadOperators = useCallback(async () => {
    try {
      const response = await fetch('/api/workflow/agenda-operatori');
      if (response.ok) setOperators(await response.json());
    } catch { /* l'Agenda resta utilizzabile anche senza l'elenco operatori */ }
  }, []);

  const load = useCallback(async () => {
    setError('');
    try {
      const mode = view === 'list' ? ({ today: 'oggi', upcoming: 'prossime', overdue: 'scadute' }[listMode]) : 'intervallo';
      const query = new URLSearchParams({ modalita: mode, limite: '250', include_completate: String(showDone) });
      if (view === 'week') {
        const first = startOfWeek(anchor); query.set('dal', localKey(first)); query.set('al', localKey(addDays(first, 6)));
      } else if (view === 'month') {
        query.set('dal', localKey(new Date(anchor.getFullYear(), anchor.getMonth(), 1)));
        query.set('al', localKey(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)));
      }
      const assignee = scope === 'mine' ? (operator?.nome || operator?.username || '') : '';
      if (type) query.set('tipo', type);
      if (assignee) query.set('assegnatario', assignee);
      const summaryQuery = new URLSearchParams();
      if (type) summaryQuery.set('tipo', type);
      if (assignee) summaryQuery.set('assegnatario', assignee);
      const [agendaResponse, summaryResponse] = await Promise.all([
        fetch(`/api/workflow/agenda?${query}`),
        fetch(`/api/workflow/agenda-riepilogo?${summaryQuery}`)
      ]);
      if (!agendaResponse.ok) {
        // Compatibilità temporanea con un server locale non ancora riavviato.
        const legacyResponse = await fetch('/api/workflow/agenda?dal=2000-01-01&al=2100-12-31');
        if (!legacyResponse.ok) throw new Error('Agenda non disponibile');
        const closed = new Set(['CHIUSA', 'SCARTATA', 'SENZA SEGUITO']);
        const allActive = (await legacyResponse.json()).filter(row => !closed.has(String(row.stato || '').trim().toUpperCase()));
        const scoped = allActive.filter(row => (!type || row.tipo === type) && (!assignee || normalize(row.assegnatario) === normalize(assignee)));
        const openRows = scoped.filter(row => !row.completata);
        setCounts({
          today: openRows.filter(row => dateKey(row.data_scadenza) === todayKey).length,
          upcoming: openRows.filter(row => dateKey(row.data_scadenza) > todayKey).length,
          overdue: openRows.filter(row => dateKey(row.data_scadenza) < todayKey).length
        });
        let visible = scoped.filter(row => showDone || !row.completata);
        if (mode === 'oggi') visible = visible.filter(row => dateKey(row.data_scadenza) === todayKey);
        if (mode === 'prossime') visible = visible.filter(row => dateKey(row.data_scadenza) > todayKey);
        if (mode === 'scadute') visible = visible.filter(row => !row.completata && dateKey(row.data_scadenza) < todayKey);
        if (mode === 'intervallo') visible = visible.filter(row => dateKey(row.data_scadenza) >= query.get('dal') && dateKey(row.data_scadenza) <= query.get('al'));
        setTotalRows(visible.length); setRows(visible.slice(0, 250));
        return;
      }
      setRows(await agendaResponse.json());
      setTotalRows(Number(agendaResponse.headers.get('X-Total-Count') || 0));
      if (summaryResponse.ok) {
        const summary = await summaryResponse.json();
        setCounts({ today: Number(summary.oggi || 0), upcoming: Number(summary.prossime || 0), overdue: Number(summary.scadute || 0) });
      }
    } catch (loadError) { setError(loadError.message); }
  }, [view, listMode, anchor, showDone, type, scope, operator, todayKey]);

  useEffect(() => { loadOperators(); }, [loadOperators]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (operators.length > 1 && operator?.nome) setScope('mine');
    else setScope('all');
  }, [operators.length, operator?.nome]);

  const types = useMemo(() => [...new Set([...TYPE_OPTIONS, ...rows.map(row => row.tipo).filter(Boolean)])].sort((a, b) => a.localeCompare(b, 'it')), [rows]);
  const baseRows = useMemo(() => rows.filter(row => {
    if (!showDone && row.completata) return false;
    if (type && row.tipo !== type) return false;
    if (scope === 'mine' && normalize(row.assegnatario) !== normalize(operator?.nome || operator?.username)) return false;
    return true;
  }), [rows, showDone, type, scope, operator]);

  const listRows = useMemo(() => baseRows.filter(row => {
    const key = dateKey(row.data_scadenza);
    if (listMode === 'today') return key === todayKey;
    if (listMode === 'upcoming') return key > todayKey;
    return !row.completata && key < todayKey;
  }), [baseRows, listMode, todayKey]);

  const groupedRows = useMemo(() => listRows.reduce((groups, row) => {
    const key = dateKey(row.data_scadenza);
    if (!groups[key]) groups[key] = [];
    groups[key].push(row);
    return groups;
  }, {}), [listRows]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(startOfWeek(anchor), index)), [anchor]);
  const monthDays = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
  }, [anchor]);

  const visibleRowsForDay = date => baseRows.filter(row => dateKey(row.data_scadenza) === localKey(date));
  const openEvent = row => {
    setSelected(row);
    setEdit({ data_scadenza: dateKey(row.data_scadenza), ora: row.ora ? String(row.ora).slice(0, 5) : '', assegnatario: row.assegnatario || '' });
  };
  const patchEvent = async changes => {
    const commitFeedback = captureUnsavedFeedback(document.querySelector('.agenda-detail')); 
    if (!selected) return;
    setBusy(true); setError('');
    try {
      const response = await fetch(`/api/workflow/scadenze/${selected.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(changes) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Attività non aggiornata');
      commitFeedback();
      setRows(current => current.map(row => row.id === result.id ? { ...row, ...result } : row));
      setSelected(previous => previous ? { ...previous, ...result } : null);
      setEdit({ data_scadenza: dateKey(result.data_scadenza), ora: result.ora ? String(result.ora).slice(0, 5) : '', assegnatario: result.assegnatario || '' });
    } catch (updateError) { setError(updateError.message); }
    finally { setBusy(false); }
  };
  const saveEdit = event => { event.preventDefault(); patchEvent(edit); };
  const shiftPeriod = amount => {
    setAnchor(current => view === 'month'
      ? new Date(current.getFullYear(), current.getMonth() + amount, 1)
      : addDays(current, amount * 7));
  };
  const periodTitle = view === 'month'
    ? `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`
    : `${formatShort(weekDays[0])} – ${formatDate(weekDays[6])}`;

  const renderEmpty = text => <div className="agenda-empty"><FaRegCalendarAlt /><strong>{text}</strong><span>Non ci sono attività da mostrare con i filtri selezionati.</span></div>;

  return <main className="page-container agenda-page">
    <header className="agenda-heading">
      <div><span className="agenda-eyebrow">ORGANIZZAZIONE DEL LAVORO</span><h1>Agenda</h1><p>Appuntamenti, scadenze e attività dello studio in un solo posto.</p></div>
      <div className="agenda-heading-date"><FaCalendarAlt /><span>Oggi</span><strong>{today.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</strong></div>
    </header>

    <section className="agenda-toolbar" aria-label="Comandi agenda">
      <div className="agenda-view-switch">
        <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}><FaList /> Elenco</button>
        <button className={view === 'week' ? 'active' : ''} onClick={() => setView('week')}><FaCalendarAlt /> Settimana</button>
        <button className={view === 'month' ? 'active' : ''} onClick={() => setView('month')}><FaRegCalendarAlt /> Mese</button>
      </div>
      <div className="agenda-toolbar-actions">
        {operators.length > 1 && <div className="agenda-scope-switch"><button className={scope === 'mine' ? 'active' : ''} onClick={() => setScope('mine')}>Le mie</button><button className={scope === 'all' ? 'active' : ''} onClick={() => setScope('all')}>Tutto lo studio</button></div>}
        <button className={filtersOpen ? 'active agenda-filter-button' : 'agenda-filter-button'} onClick={() => setFiltersOpen(value => !value)}><FaFilter /> Filtri</button>
      </div>
    </section>

    {filtersOpen && <section className="agenda-filters">
      <label>Tipo di attività<select value={type} onChange={event => setType(event.target.value)}><option value="">Tutte le attività</option>{types.map(value => <option key={value}>{value}</option>)}</select></label>
      <label className="agenda-check"><input type="checkbox" checked={showDone} onChange={event => setShowDone(event.target.checked)} /> Mostra attività completate</label>
      <button type="button" onClick={() => { setType(''); setShowDone(false); }}>Azzera filtri</button>
    </section>}

    {error && <div className="agenda-error" role="alert"><FaExclamationTriangle /> {error}</div>}

    {view === 'list' && <>
      <nav className="agenda-status-tabs" aria-label="Periodo attività">
        <button className={listMode === 'today' ? 'active' : ''} onClick={() => setListMode('today')}><span>Oggi</span><strong>{counts.today}</strong></button>
        <button className={listMode === 'upcoming' ? 'active' : ''} onClick={() => setListMode('upcoming')}><span>Prossime</span><strong>{counts.upcoming}</strong></button>
        <button className={`overdue ${listMode === 'overdue' ? 'active' : ''}`} onClick={() => setListMode('overdue')}><span>Scadute</span><strong>{counts.overdue}</strong></button>
      </nav>
      <section className="agenda-list">
        {!listRows.length ? renderEmpty(listMode === 'today' ? 'Nessuna attività per oggi' : listMode === 'upcoming' ? 'Nessuna attività futura' : 'Nessuna attività scaduta') :
          Object.entries(groupedRows).map(([day, events]) => <div className="agenda-day-group" key={day}>
            <div className="agenda-day-label"><span>{parseDate(day).toLocaleDateString('it-IT', { weekday: 'long' })}</span><strong>{formatDate(day)}</strong></div>
            <div className="agenda-day-events">{events.map(row => <AgendaEvent key={row.id} row={row} onOpen={openEvent} />)}</div>
          </div>)}
      </section>
      {totalRows > rows.length && <p className="agenda-result-limit">Sono mostrate le prime {rows.length} attività su {totalRows}. Usa i filtri per restringere il risultato.</p>}
    </>}

    {view !== 'list' && <>
      <div className="agenda-period-nav"><button aria-label="Periodo precedente" onClick={() => shiftPeriod(-1)}><FaChevronLeft /></button><button onClick={() => setAnchor(new Date())}>Oggi</button><h2>{periodTitle}</h2><button aria-label="Periodo successivo" onClick={() => shiftPeriod(1)}><FaChevronRight /></button></div>
      {view === 'week' && <section className="agenda-week-grid">
        {weekDays.map(day => <div className={`agenda-calendar-day ${localKey(day) === todayKey ? 'is-today' : ''}`} key={localKey(day)}>
          <header><span>{day.toLocaleDateString('it-IT', { weekday: 'short' })}</span><strong>{day.getDate()}</strong></header>
          <div>{visibleRowsForDay(day).map(row => <AgendaEvent compact key={row.id} row={row} onOpen={openEvent} />)}{!visibleRowsForDay(day).length && <span className="agenda-no-events">Nessuna attività</span>}</div>
        </div>)}
      </section>}
      {view === 'month' && <section className="agenda-month-wrap"><div className="agenda-month-head">{WEEK_DAYS.map(day => <span key={day}>{day}</span>)}</div><div className="agenda-month-grid">
        {monthDays.map(day => { const events = visibleRowsForDay(day); return <div className={`agenda-month-day ${day.getMonth() !== anchor.getMonth() ? 'is-outside' : ''} ${localKey(day) === todayKey ? 'is-today' : ''}`} key={localKey(day)}>
          <strong>{day.getDate()}</strong><div>{events.slice(0, 3).map(row => <button key={row.id} className={`agenda-month-event agenda-month-event--${category(row.tipo)}`} onClick={() => openEvent(row)} title={row.descrizione || row.tipo}>{row.ora ? String(row.ora).slice(0, 5) : '•'} {row.descrizione || row.tipo}</button>)}{events.length > 3 && <button className="agenda-more" onClick={() => { setAnchor(day); setView('week'); }}>+{events.length - 3} altre</button>}</div>
        </div>; })}
      </div></section>}
    </>}

    {selected && edit && <div className="agenda-overlay" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setSelected(null); }}>
      <section className="agenda-detail" role="dialog" aria-modal="true" aria-labelledby="agenda-detail-title">
        <header><div><span className={`agenda-detail-type agenda-detail-type--${category(selected.tipo)}`}>{selected.tipo}</span><h2 id="agenda-detail-title">{selected.descrizione || selected.tipo}</h2></div><button className="agenda-close" aria-label="Chiudi" onClick={() => setSelected(null)}><FaTimes /></button></header>
        <div className="agenda-detail-practice"><div><FaUser /><span>Cliente</span><strong>{personName(selected)}</strong></div><div><FaRegCalendarAlt /><span>Pratica</span><strong>{selected.numero_progressivo || 'Non indicata'}</strong></div></div>
        <form onSubmit={saveEdit} className="agenda-detail-form">
          <label>Data<input required type="date" value={edit.data_scadenza} onChange={event => setEdit(current => ({ ...current, data_scadenza: event.target.value }))} /></label>
          <label>Ora<input type="time" value={edit.ora} onChange={event => setEdit(current => ({ ...current, ora: event.target.value }))} /></label>
          {operators.length > 1 && <label className="wide">Operatore<select value={edit.assegnatario} onChange={event => setEdit(current => ({ ...current, assegnatario: event.target.value }))}><option value="">Non assegnata</option>{operators.map(item => <option key={item.id} value={item.nome}>{item.nome}</option>)}</select></label>}
          {selected.assegnatario && operators.length <= 1 && <p className="agenda-assignee"><FaUser /> Assegnata a {selected.assegnatario}</p>}
          <button className="agenda-save-date" type="submit" disabled={busy}><FaClock /> Salva data e assegnazione</button>
        </form>
        <footer>
          <Link className="agenda-open-practice" to={`/pratica/${selected.scheda_id}`}>Apri pratica</Link>
          <a className="agenda-outlook" href={`/api/workflow/scadenze/${selected.id}/ics`}><FaDownload /> Outlook</a>
          <button className={selected.completata ? 'agenda-reopen' : 'agenda-complete'} disabled={busy} onClick={() => patchEvent({ completata: !selected.completata })}><FaCheck /> {selected.completata ? 'Riapri attività' : 'Completa'}</button>
        </footer>
        {selected.tipo === 'Prescrizione' && <div className="agenda-prescription"><strong>Prescrizione</strong><span>Questa attività richiede particolare attenzione.</span><a href={`/api/workflow/scadenze/${selected.id}/interruzione-email`}>Prepara interruzione</a></div>}
      </section>
    </div>}
  </main>;
}
